import { Link } from 'react-router-dom';
import { CircuitBoardSVG, IoTNodesSVG, NeuralNetSVG } from '../components/TechIllustrations';

const poles = [
  {
    number: '01', color: 'green' as const,
    title: 'IoT & Connected Solutions',
    subtitle: 'Smart infrastructure for a connected Africa',
    desc: 'All BETRobionics capabilities in sensor networks, connected systems and real-time data platforms. From smart farms to smart cities, we deploy IoT infrastructure that works reliably in African environments.',
    features: [
      'Wireless sensor networks (LoRa, WiFi, BLE, Zigbee)',
      'Real-time data dashboards and alerting',
      'Smart agriculture, buildings, energy & safety',
      'Edge computing for low-connectivity areas',
      'Cloud integration and remote management',
    ],
    services: ['IoT Solutions', 'Consulting & Integration'],
    illustration: <IoTNodesSVG className="w-full" />,
    ilLabel: 'IoT Sensor Network Topology',
  },
  {
    number: '02', color: 'red' as const,
    title: 'Electronics, Robotics & AI',
    subtitle: 'Hardware and intelligence, built from the ground up',
    desc: 'Our technology pole brings together electronics engineering, robotics manufacturing, and artificial intelligence. We design circuits, program microcontrollers, build robots, and train AI models — all under one roof.',
    features: [
      'PCB design, prototyping and manufacturing',
      'Embedded systems and firmware development',
      'Industrial and educational robotics',
      'Machine learning and computer vision',
      'Electronics repair and maintenance',
    ],
    services: ['Electronics & Embedded', 'Robotics'],
    illustration: <CircuitBoardSVG className="w-full" />,
    ilLabel: 'Custom PCB Design',
  },
  {
    number: '03', color: 'green' as const,
    title: 'Training, Consulting & Commerce',
    subtitle: 'Knowledge transfer and technology access for all',
    desc: 'The third pole ensures technology reaches everyone — through training programs that build skills, consulting that guides organizations, and an e-commerce platform that makes quality tech products accessible across East Africa.',
    features: [
      'Youth and women empowerment programs',
      'Professional and university-certified courses',
      'IoT and robotics strategy consulting',
      'Smart city and integration projects',
      'Online store with local and international delivery',
    ],
    services: ['Training & Capacity Building', 'Consulting & Integration', 'E-commerce'],
    illustration: <NeuralNetSVG className="w-full" />,
    ilLabel: 'AI Training Models',
  },
];

const PolesPage = () => (
  <div className="bg-white">

    {/* ── Page header ── */}
    <div className="border-b border-slate-200 bg-slate-50 py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <span className="section-label">Our Three Poles</span>
        <h1 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">Core Areas of Excellence</h1>
        <p className="mt-3 max-w-2xl text-slate-500 leading-6">
          BETRobionics is organized around three complementary poles that together cover
          every dimension of smart technology — from hardware to software to human capital.
        </p>
      </div>
    </div>

    {/* ── Poles ── */}
    <div>
      {poles.map((pole, i) => {
        const isGreen = pole.color === 'green';
        const ac = isGreen ? '#1aa534' : '#d2252e';
        return (
          <section key={pole.number} className={`py-16 border-b border-slate-100 ${i % 2 !== 0 ? 'bg-slate-50' : 'bg-white'}`}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className={`grid gap-12 lg:grid-cols-2 lg:items-start ${i % 2 !== 0 ? 'lg:grid-flow-dense' : ''}`}>

                {/* Text */}
                <div className={i % 2 !== 0 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-6xl font-black" style={{ color: `${ac}18` }}>{pole.number}</span>
                    <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest"
                      style={{ background: `${ac}12`, color: ac }}>
                      Pole {pole.number}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">{pole.title}</h2>
                  <p className="mt-1 text-sm font-medium" style={{ color: ac }}>{pole.subtitle}</p>
                  <p className="mt-4 text-slate-500 leading-7">{pole.desc}</p>

                  <div className={`mt-6 rounded-2xl border p-6`}
                    style={{ borderColor: `${ac}25`, background: `${ac}04` }}>
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Key capabilities</p>
                    <ul className="space-y-2.5">
                      {pole.features.map(f => (
                        <li key={f} className="flex items-start gap-3">
                          <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                            style={{ background: `${ac}18` }}>
                            <svg className="h-3 w-3" style={{ color: ac }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          </div>
                          <span className="text-sm text-slate-600 leading-6">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {pole.services.map(s => (
                      <Link key={s} to="/services"
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:text-[#1aa534] hover:border-[#1aa534] transition-colors shadow-sm">
                        {s}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Illustration */}
                <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${i % 2 !== 0 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: ac }}>{pole.ilLabel}</p>
                  {pole.illustration}
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>

    {/* ── CTA ── */}
    <section className="bg-[#1aa534] py-14">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-white sm:text-3xl">Ready to work with us?</h2>
        <p className="mt-3 text-green-100 leading-7">
          Our three poles work together to deliver complete, integrated technology solutions.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="rounded-lg bg-white px-8 py-3.5 text-sm font-semibold text-[#1aa534] hover:bg-slate-50 transition-colors">
            Contact Our Team
          </Link>
          <Link to="/services" className="rounded-lg border-2 border-white/40 px-8 py-3.5 text-sm font-semibold text-white hover:border-white transition-colors">
            View All Services
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default PolesPage;
